import React, { useEffect, useState, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSlideNavigation } from '../hooks/useSlideNavigation';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
// Import Slides
import { TitleSlide } from './slides/TitleSlide';
import { AboutSlide } from './slides/AboutSlide';
import { ProblemSlide } from './slides/ProblemSlide';
import { SolutionSlide } from './slides/SolutionSlide';
import { ServicesSlide } from './slides/ServicesSlide';
import { ProcessSlide } from './slides/ProcessSlide';
import { WhySlide } from './slides/WhySlide';
import { IndustriesSlide } from './slides/IndustriesSlide';
import { VisionSlide } from './slides/VisionSlide';
import { ContactSlide } from './slides/ContactSlide';
const SLIDES = [TitleSlide, AboutSlide, ProblemSlide, SolutionSlide, ServicesSlide, ProcessSlide, WhySlide, IndustriesSlide, VisionSlide, ContactSlide];
export function Presentation() {
  const {
    currentSlide,
    direction,
    nextSlide,
    prevSlide,
    goToSlide,
    isFirst,
    isLast
  } = useSlideNavigation(SLIDES.length);
  const CurrentSlideComponent = SLIDES[currentSlide];
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  const [showHint, setShowHint] = useState(true);
  // Auto-dismiss hint after 6 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 6000);
    return () => clearTimeout(timer);
  }, []);
  // Dismiss hint on first navigation
  useEffect(() => {
    if (currentSlide > 0) {
      setShowHint(false);
    }
  }, [currentSlide]);
  return <div className="w-screen h-screen bg-bg overflow-hidden relative" data-id="element-1">
      {/* Progress Bar */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gray-200 z-50" data-id="element-2">
        <motion.div className="h-full bg-accent" initial={{
        width: 0
      }} animate={{
        width: `${(currentSlide + 1) / SLIDES.length * 100}%`
      }} transition={{
        duration: 0.3
      }} data-id="element-3" />
      </div>

      {/* Main Slide Area */}
      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-4">
        <motion.div key={currentSlide} custom={direction} variants={{
        enter: (direction: number) => ({
          x: direction > 0 ? '100%' : '-100%',
          opacity: 0,
          zIndex: 0
        }),
        center: {
          x: 0,
          opacity: 1,
          zIndex: 1
        },
        exit: (direction: number) => ({
          x: direction > 0 ? '-100%' : '100%',
          opacity: 0,
          zIndex: 0
        })
      }} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-5">
          <CurrentSlideComponent data-id="element-6" />
        </motion.div>
      </AnimatePresence>

      {/* Navigation Controls */}
      <div className="absolute bottom-8 left-0 w-full flex flex-col items-center gap-4 z-50 pointer-events-none" data-id="element-7">
        {/* Dots */}
        <div className="flex gap-3 pointer-events-auto" data-id="element-8">
          {SLIDES.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className={`h-2 rounded-full transition-all duration-300 ${currentSlide === index ? 'w-8 bg-accent' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-9" />)}
        </div>
      </div>

      {/* Side Arrows */}
      {!isFirst && <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-text-secondary hover:text-primary backdrop-blur-sm transition-all z-50" data-id="element-10">
          <ChevronLeft className="w-8 h-8" data-id="element-11" />
        </button>}

      {!isLast && <div className="absolute right-4 top-1/2 -translate-y-1/2 z-50 flex items-center" data-id="element-12">
          {/* Navigation Hint Tooltip */}
          <AnimatePresence data-id="element-13">
            {showHint && isFirst && <motion.div initial={{
          opacity: 0,
          x: 10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 10
        }} transition={{
          delay: 1.5,
          duration: 0.4
        }} className="mr-3 flex items-center" data-id="element-14">
                <div className="bg-white/95 backdrop-blur-sm text-primary text-sm font-medium px-4 py-2.5 rounded-xl shadow-lg border border-gray-100 whitespace-nowrap" data-id="element-15">
                  Hier klicken zum Weiterblättern →
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Arrow Button with pulse ring on first slide */}
          <button onClick={() => {
        setShowHint(false);
        nextSlide();
      }} className="relative p-3 rounded-full bg-white/10 hover:bg-white/20 text-text-secondary hover:text-primary backdrop-blur-sm transition-all" data-id="element-16">
            {/* Pulsing ring — stays visible on first slide even after tooltip fades */}
            {isFirst && <motion.div className="absolute inset-0 rounded-full border-2 border-accent" animate={{
          opacity: [0, 0.8, 0],
          scale: [1, 1.5, 1.8]
        }} transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: 'easeOut'
        }} data-id="element-17" />}
            <ChevronRight className="w-8 h-8 relative z-10" data-id="element-18" />
          </button>
        </div>}

      {/* Legal Links — bottom right on every slide */}
      <div className="absolute bottom-3 right-4 z-50 flex items-center gap-3" data-id="element-19">
        <button onClick={() => setShowDatenschutz(true)} className="text-[11px] text-text-muted/60 hover:text-text-secondary transition-colors" data-id="element-20">
          Datenschutz
        </button>
        <span className="text-[11px] text-text-muted/30" data-id="element-21">|</span>
        <button onClick={() => setShowImpressum(true)} className="text-[11px] text-text-muted/60 hover:text-text-secondary transition-colors" data-id="element-22">
          Impressum
        </button>
      </div>

      {/* Impressum Modal */}
      <AnimatePresence data-id="element-23">
        {showImpressum && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-24">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowImpressum(false)} data-id="element-25" />
            <motion.div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-8" initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} data-id="element-26">
              <button onClick={() => setShowImpressum(false)} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 text-text-muted hover:text-primary transition-colors" data-id="element-27">
                <X className="w-5 h-5" data-id="element-28" />
              </button>
              <h2 className="text-2xl font-bold text-primary mb-6" data-id="element-29">
                Impressum
              </h2>
              <div className="space-y-5 text-sm text-text-secondary leading-relaxed" data-id="element-30">
                <div data-id="element-31">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-32">
                    Angaben gemäß § 5 TMG
                  </h3>
                  <p data-id="element-33">
                    INTIGOO! GmbH
                    <br data-id="element-34" />
                    Werner-von-Siemens-Straße 33
                    <br data-id="element-35" />
                    49124 Georgsmarienhütte
                  </p>
                </div>
                <div data-id="element-36">
                  <p data-id="element-37">
                    Handelsregister: HRB 217224
                    <br data-id="element-38" />
                    Registergericht: Amtsgericht Osnabrück
                  </p>
                </div>
                <div data-id="element-39">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-40">
                    Vertreten durch
                  </h3>
                  <p data-id="element-41">
                    Fabian Kleine Huxel
                    <br data-id="element-42" />
                    Felix Höfelmeyer
                  </p>
                </div>
                <div data-id="element-43">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-44">Kontakt</h3>
                  <p data-id="element-45">
                    Telefon: +49 5401 1689580
                    <br data-id="element-46" />
                    E‑Mail: info@intigoo.de
                  </p>
                </div>
                <div data-id="element-47">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-48">
                    Umsatzsteuer-ID
                  </h3>
                  <p data-id="element-49">
                    Umsatzsteuer-Identifikationsnummer gemäß § 27 a
                    Umsatzsteuergesetz:
                    <br data-id="element-50" />
                    DE 349819653
                  </p>
                </div>
                <div data-id="element-51">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-52">
                    EU-Streitschlichtung
                  </h3>
                  <p data-id="element-53">
                    Die Europäische Kommission stellt eine Plattform zur
                    Online-Streitbeilegung (OS) bereit:{' '}
                    <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline" data-id="element-54">
                      https://ec.europa.eu/consumers/odr/
                    </a>
                    <br data-id="element-55" />
                    Unsere E‑Mail-Adresse finden Sie oben im Impressum.
                  </p>
                </div>
                <div data-id="element-56">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-57">
                    Verbraucherstreitbeilegung / Universalschlichtungsstelle
                  </h3>
                  <p data-id="element-58">
                    Wir sind nicht bereit oder verpflichtet, an
                    Streitbeilegungsverfahren vor einer
                    Verbraucherschlichtungsstelle teilzunehmen.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Datenschutz Modal */}
      <AnimatePresence data-id="element-59">
        {showDatenschutz && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-60">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDatenschutz(false)} data-id="element-61" />
            <motion.div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-8" initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} data-id="element-62">
              <button onClick={() => setShowDatenschutz(false)} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 text-text-muted hover:text-primary transition-colors" data-id="element-63">
                <X className="w-5 h-5" data-id="element-64" />
              </button>
              <h2 className="text-2xl font-bold text-primary mb-6" data-id="element-65">
                Datenschutzerklärung
              </h2>
              <div className="space-y-5 text-sm text-text-secondary leading-relaxed" data-id="element-66">
                <div data-id="element-67">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-68">
                    1. Verantwortlicher
                  </h3>
                  <p data-id="element-69">
                    Die verantwortliche Stelle für die Datenverarbeitung auf
                    dieser Website ist:
                  </p>
                  <p className="mt-2" data-id="element-70">
                    INTIGOO! GmbH
                    <br data-id="element-71" />
                    Werner-von-Siemens-Straße 33
                    <br data-id="element-72" />
                    49124 Georgsmarienhütte
                  </p>
                  <p className="mt-2" data-id="element-73">
                    Telefon: +49 5401 4977 0<br data-id="element-74" />
                    E‑Mail: info@intigoo.de
                  </p>
                  <p className="mt-2" data-id="element-75">
                    Verantwortliche Stelle ist die natürliche oder juristische
                    Person, die allein oder gemeinsam mit anderen über die
                    Zwecke und Mittel der Verarbeitung von personenbezogenen
                    Daten (z. B. Namen, E‑Mail-Adressen o. Ä.) entscheidet.
                  </p>
                </div>
                <div data-id="element-76">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-77">
                    2. Hosting
                  </h3>
                  <p data-id="element-78">
                    Diese Website wird bei Amazon Web Services (AWS) gehostet.
                  </p>
                  <p className="mt-2" data-id="element-79">
                    Anbieter in Europa ist:
                    <br data-id="element-80" />
                    Amazon Web Services EMEA SARL
                    <br data-id="element-81" />
                    38 Avenue John F. Kennedy
                    <br data-id="element-82" />
                    L-1855 Luxemburg
                  </p>
                  <p className="mt-2" data-id="element-83">
                    Beim Aufruf dieser Website werden durch den Hosting-Anbieter
                    automatisch folgende Daten verarbeitet:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1" data-id="element-84">
                    <li data-id="element-85">IP-Adresse</li>
                    <li data-id="element-86">Datum und Uhrzeit des Zugriffs</li>
                    <li data-id="element-87">Browsertyp und Browserversion</li>
                    <li data-id="element-88">verwendetes Betriebssystem</li>
                    <li data-id="element-89">Referrer-URL</li>
                    <li data-id="element-90">Hostname des zugreifenden Rechners</li>
                  </ul>
                  <p className="mt-2" data-id="element-91">
                    Diese Daten werden in sogenannten Server-Logfiles
                    gespeichert.
                  </p>
                  <p className="mt-2" data-id="element-92">
                    Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1
                    lit. f DSGVO (berechtigtes Interesse an der sicheren und
                    stabilen Bereitstellung der Website).
                  </p>
                  <p className="mt-2" data-id="element-93">
                    Mit AWS besteht ein Vertrag zur Auftragsverarbeitung gemäß
                    Art. 28 DSGVO.
                  </p>
                  <p className="mt-2" data-id="element-94">
                    Sofern eine Verarbeitung außerhalb der Europäischen Union
                    erfolgt, geschieht dies auf Grundlage geeigneter Garantien
                    gemäß Art. 44 ff. DSGVO (insbesondere
                    EU-Standardvertragsklauseln).
                  </p>
                </div>
                <div data-id="element-95">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-96">
                    3. Cookies
                  </h3>
                  <p data-id="element-97">
                    Diese Website verwendet ausschließlich technisch notwendige
                    Cookies, soweit dies für den Betrieb erforderlich ist.
                  </p>
                  <p className="mt-2" data-id="element-98">
                    Es werden keine Analyse-, Tracking- oder Marketing-Cookies
                    eingesetzt.
                  </p>
                  <p className="mt-2" data-id="element-99">
                    Eine Einwilligung über ein Cookie-Banner ist daher nicht
                    erforderlich.
                  </p>
                </div>
                <div data-id="element-100">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-101">
                    4. Speicherdauer
                  </h3>
                  <p data-id="element-102">
                    Server-Logfiles werden vom Hosting-Anbieter automatisch nach
                    einem angemessenen Zeitraum gelöscht.
                  </p>
                </div>
                <div data-id="element-103">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-104">
                    5. Ihre Rechte
                  </h3>
                  <p data-id="element-105">Sie haben das Recht auf:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1" data-id="element-106">
                    <li data-id="element-107">Auskunft (Art. 15 DSGVO)</li>
                    <li data-id="element-108">Berichtigung (Art. 16 DSGVO)</li>
                    <li data-id="element-109">Löschung (Art. 17 DSGVO)</li>
                    <li data-id="element-110">Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                    <li data-id="element-111">Datenübertragbarkeit (Art. 20 DSGVO)</li>
                    <li data-id="element-112">Widerspruch (Art. 21 DSGVO)</li>
                  </ul>
                  <p className="mt-2" data-id="element-113">
                    Zur Ausübung Ihrer Rechte genügt eine formlose Mitteilung an
                    die oben genannten Kontaktdaten.
                  </p>
                </div>
                <div data-id="element-114">
                  <h3 className="font-semibold text-primary mb-1" data-id="element-115">
                    6. Beschwerderecht
                  </h3>
                  <p data-id="element-116">
                    Sie haben das Recht, sich bei einer
                    Datenschutz-Aufsichtsbehörde zu beschweren.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}